import React, { useContext, useMemo, useCallback, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import { jsx } from 'react/jsx-runtime';
import { useLocation, useRouter, Router } from 'wouter';
import { compile, pathToRegexp } from 'path-to-regexp';
import { navigate } from 'wouter/use-browser-location';
import mitt from 'mitt';

/* eslint-disable react/jsx-props-no-spreading */
const RoutesContext = /*#__PURE__*/React.createContext(null);
const useRoutesContext = () => useContext(RoutesContext);
const propTypes$1 = {
  children: PropTypes.node.isRequired,
  routes: PropTypes.objectOf(PropTypes.string).isRequired,
  basePath: PropTypes.string
};
const defaultProps = {
  basePath: null
};
function RoutesProvider(_ref) {
  let {
    routes,
    basePath,
    children
  } = _ref;
  const value = useMemo(() => ({
    routes,
    basePath
  }), [routes, basePath]);
  return /*#__PURE__*/jsx(RoutesContext.Provider, {
    value: value,
    children: children
  });
}
RoutesProvider.propTypes = propTypes$1;
RoutesProvider.defaultProps = defaultProps;

const compilers = {};
function generatePath(fullPath, data) {
  let opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const fullUrlMatches = fullPath.match(/^(https?:\/\/)/);
  if (typeof compilers[fullPath] === 'undefined') {
    compilers[fullPath] = compile(fullPath.replace(/^(https?:\/\/)/, ''), opts);
  }
  const compiler = compilers[fullPath];
  return fullUrlMatches !== null ? `${fullUrlMatches[1]}${compiler(data)}` : compiler(data);
}

const useUrlGeneratorPathToRepexp = () => {
  const {
    routes = null,
    basePath = null
  } = useRoutesContext() || {};
  const urlGenerator = useCallback((key, data, opts) => {
    const path = routes !== null ? routes[key] || null : null;
    if (path === null) {
      return null;
    }
    const url = generatePath(path, data, opts);
    return basePath !== null ? `${basePath.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url;
  }, [routes, basePath]);
  return urlGenerator;
};

const useRouteNavigate = () => {
  const url = useUrlGeneratorPathToRepexp();
  const [, setLocation] = useLocation();
  const routeNavigate = useCallback(function (route, data) {
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }
    return setLocation(url(route, data), ...args);
  }, [setLocation, url]);
  return routeNavigate;
};

const useRoutes = () => {
  const {
    routes
  } = useRoutesContext();
  return routes;
};

const propTypes = {
  location: PropTypes.string.isRequired,
  search: PropTypes.string,
  children: PropTypes.node.isRequired
};
function StaticRouter(_ref) {
  let {
    location,
    search = null,
    children
  } = _ref;
  const hook = useCallback(() => [location.split('?')[0], navigate], [location]);
  const searchHook = useCallback(() => search ?? (location.indexOf('?') !== -1 ? location.split('?')[1] : ''), [search, location]);
  const {
    parser
  } = useRouter();
  return /*#__PURE__*/jsx(Router, {
    parser: parser,
    hook: hook,
    searchHook: searchHook,
    children: children
  });
}
StaticRouter.propTypes = propTypes;

function parseLocation(location) {
  let search = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let hash = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const [pathname, searchFromPath = null] = (location || '').split('?', 2);
  const [searchWithoutHash, hashFromPath = null] = (searchFromPath || '').split('#', 2);
  return {
    pathname: pathname !== '' ? pathname : '/',
    search: search || searchWithoutHash || null,
    hash: hash || hashFromPath || null
  };
}

function useMemoryRouter() {
  let {
    path = '/',
    static: staticLocation = false,
    record = true
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let currentPath = parseLocation(path);
  const emitter = mitt();
  const navigateImplementation = function (newPath) {
    let {
      replace = false
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const newParsedPath = parseLocation(newPath);
    currentPath = newParsedPath;
    emitter.emit('navigate', path);
  };
  const navigate = !staticLocation ? navigateImplementation : () => null;
  const subscribe = cb => {
    emitter.on('navigate', cb);
    return () => emitter.off('navigate', cb);
  };
  function reset() {
    navigateImplementation(path);
  }
  const locationHook = () => [useSyncExternalStore(subscribe, () => currentPath.pathname), navigate];
  const searchHook = () => useSyncExternalStore(subscribe, () => currentPath.search || '');
  return {
    hook: locationHook,
    searchHook,
    reset,
    navigate
  };
}

function useRouteMatcher() {
  const router = useRouter();
  const [location] = useLocation();
  const matcher = useCallback(function (route) {
    let specificLocation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    const path = specificLocation || location;
    // when parser is in "loose" mode, `$base` is equal to the
    // first part of the route that matches the pattern
    // (e.g. for pattern `/a/:b` and path `/a/1/2/3` the `$base` is `a/1`)
    // we use this for route nesting
    const {
      pattern,
      keys
    } = router.parser(route || '*');
    const [$base, ...matches] = pattern.exec(path) || [];
    return $base !== undefined ? [true,
    // an object with parameters matched, e.g. { foo: "bar" } for "/:foo"
    // we "zip" two arrays here to construct the object
    // ["foo"], ["bar"] → { foo: "bar" }
    Object.fromEntries(keys.map((key, i) => [key, matches[i]]))] : [false];
  }, [router, location]);
  return matcher;
}

function createPathToRegexpParser(opts) {
  return (fullPath, loose) => {
    const path = fullPath.replace(/^(https?:\/\/[^/]+)\/?/, '/');
    const keys = [];
    const isWildcard = path.match(/(\/|^)\*$/) !== null;
    const pattern = pathToRegexp(isWildcard ? path.replace(/(\/|^)\*$/, '$1(.*)') : path, keys, {
      end: !loose && !isWildcard,
      ...opts
    });
    return {
      pattern,
      // `pathToRegexp` returns some metadata about the keys,
      // we want to strip it to just an array of keys
      keys: keys.map(k => k.name)
    };
  };
}

export { RoutesContext, RoutesProvider, StaticRouter, createPathToRegexpParser, generatePath, useMemoryRouter, useRouteMatcher, useRouteNavigate, useRoutes, useRoutesContext, useUrlGeneratorPathToRepexp as useUrlGenerator };
